/**
 * useTypography — the reader's preferences, the fonts, and `data-measure`.
 *
 * design.md §5.8. books' `reader.css` and `tokens.css` are lifted whole (see the provenance
 * headers in ui/), so this file does not decide what the type looks like — it decides which of the
 * lifted knobs are turned, remembers the answer, and reports the two measurements pagination needs
 * (`ch` and `em`, in the face actually in use).
 *
 * ⚠️ **Fonts must be `document.fonts.ready` BEFORE measuring** or the first pagination is computed
 * against the fallback face and every page count is wrong — and it is wrong *silently*, because
 * the reader lands on a real page, just not the one the sid is on. `useTypography` exposes
 * `fontsReady`; `Reader.tsx` gates its first `repaginate()` on it and repaginates again with cause
 * `"fonts"` if the flag flips late.
 *
 * ⚠️ **Never `font-variation-settings` for `wght`.** It takes the axis out of the cascade, so a
 * descendant setting `font-weight` is ignored and `font-optical-sizing: auto` silently stops
 * responding to font-size — which is the entire reason Literata was chosen. (books.karti.ai
 * design-system §3.2.) Nothing in this file sets it; nothing in ui/ does either.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SpreadPref } from "./Paginator.ts";

/* ══════════════════════════════════════════════════════════════════════════
 * The knobs
 * ══════════════════════════════════════════════════════════════════════════ */

export type ReaderFontKey = "literata" | "modern" | "classic" | "accessible" | "sans";

export interface ReaderFontOption {
  key: ReaderFontKey;
  label: string;
  /** What the label means, shown under it. A font picker with five serif names is a guessing game. */
  note: string;
  /** The family names the stack asks for, in order — what `document.fonts.load` is given. */
  families: readonly string[];
}

export const READER_FONTS: readonly ReaderFontOption[] = [
  { key: "literata", label: "Literata", note: "The default. Drawn for ebooks.", families: ["Literata Variable"] },
  { key: "modern", label: "Modern", note: "Source Serif — crisper, more open.", families: ["Source Serif 4 Variable"] },
  { key: "classic", label: "Classic", note: "EB Garamond — old-style, good for verse.", families: ["EB Garamond Variable"] },
  {
    key: "accessible",
    label: "Accessible",
    note: "Atkinson Hyperlegible — confusable letters kept apart.",
    families: ["Atkinson Hyperlegible Next", "Atkinson Hyperlegible"],
  },
  { key: "sans", label: "Sans", note: "Instrument Sans.", families: ["Instrument Sans Variable"] },
];

/**
 * The seven steps, copied from the comment beside `--read-scale` in books' lifted `reader.css`.
 * They are steps and not a slider because a reading size is a *choice between good sizes*, and a
 * continuous control invites 1.03, which is a worse line than either neighbour.
 */
export const READER_SCALES = [0.85, 0.925, 1, 1.075, 1.15, 1.3, 1.5] as const;
export const DEFAULT_SCALE_INDEX = 2; // 1.0

/** Orthogonal to the app theme, by design: a night owl reads sepia in a light UI. */
export type ReaderTheme = "auto" | "day" | "sepia" | "night" | "paper";
export const READER_THEMES: readonly { key: ReaderTheme; label: string }[] = [
  { key: "auto", label: "Auto" },
  { key: "day", label: "Day" },
  { key: "sepia", label: "Sepia" },
  { key: "night", label: "Night" },
  { key: "paper", label: "Paper" },
];

export interface TypographyPrefs {
  font: ReaderFontKey;
  /** Index into READER_SCALES. An index and not a float, so the steps stay the measured ones. */
  scaleIndex: number;
  theme: ReaderTheme;
  /** §5.8: "justification a preference, default off, forcing hyphenation when enabled." */
  justify: boolean;
  /** §5.1: spread is automatic on wide screens and user-overridable. */
  spread: SpreadPref;
}

export const DEFAULT_PREFS: TypographyPrefs = {
  font: "literata",
  scaleIndex: DEFAULT_SCALE_INDEX,
  theme: "auto",
  justify: false,
  spread: "auto",
};

const PREFS_KEY = "ab.reader.prefs";

function loadPrefs(): TypographyPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw) as Partial<TypographyPrefs>;
    return {
      font: READER_FONTS.some((f) => f.key === p.font) ? (p.font as ReaderFontKey) : DEFAULT_PREFS.font,
      scaleIndex:
        typeof p.scaleIndex === "number" && p.scaleIndex >= 0 && p.scaleIndex < READER_SCALES.length
          ? Math.trunc(p.scaleIndex)
          : DEFAULT_PREFS.scaleIndex,
      theme: READER_THEMES.some((t) => t.key === p.theme) ? (p.theme as ReaderTheme) : DEFAULT_PREFS.theme,
      justify: p.justify === true,
      spread: p.spread === "single" || p.spread === "spread" ? p.spread : "auto",
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(p: TypographyPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* Private browsing. The preference is lost on reload; the reader still works. */
  }
}

/* ══════════════════════════════════════════════════════════════════════════
 * Measurement
 * ══════════════════════════════════════════════════════════════════════════ */

export interface FontMetrics {
  /** Width of one `ch` in px, as the browser resolves the unit in the face actually in use. */
  chPx: number;
  /** Width of one `em` in px — i.e. the computed font-size of the reading column. */
  emPx: number;
  /** `--read-measure` in ch (66 in books' lifted reader.css). */
  measureCh: number;
  /** `--read-measure-max` in em (40 — past this a column becomes a spread, never wider). */
  measureMaxEm: number;
}

const FALLBACK_METRICS: FontMetrics = { chPx: 8.5, emPx: 17, measureCh: 66, measureMaxEm: 40 };

/**
 * Read the metrics off the `ch` probe — a `width: 100ch` element carrying `.read`, so it resolves
 * `--font-read`, `--read-size` and `--read-scale` to exactly the values the text does.
 *
 * Returns the fallback rather than throwing if the probe is not laid out yet (a detached node, or
 * the frame is `display: none` in a hidden tab). A pagination computed from the fallback is wrong,
 * which is why the caller repaginates on `fontsReady` — but a reader that throws during layout is
 * a white screen.
 */
export function readFontMetrics(probe: HTMLElement | null): FontMetrics {
  if (!probe) return FALLBACK_METRICS;
  const rect = probe.getBoundingClientRect();
  const cs = getComputedStyle(probe);
  const chPx = rect.width > 0 ? rect.width / 100 : FALLBACK_METRICS.chPx;
  const emPx = parseFloat(cs.fontSize) || FALLBACK_METRICS.emPx;
  const measureCh = parseFloat(cs.getPropertyValue("--read-measure")) || FALLBACK_METRICS.measureCh;
  const measureMaxEm =
    parseFloat(cs.getPropertyValue("--read-measure-max")) || FALLBACK_METRICS.measureMaxEm;
  return { chPx, emPx, measureCh, measureMaxEm };
}

export type MeasureBucket = "narrow" | "normal" | "wide";

/**
 * §5.8: "leading keys off the **column** (the frame carries `data-measure="narrow|normal|wide"`
 * written by the ResizeObserver from the computed column width in `ch`, because container queries
 * don't reach cleanly inside a fragmentation context)".
 *
 * The thresholds are where the classic leading rule changes its answer: below ~48 characters a
 * line is short enough that tight leading still tracks; above ~68 the eye needs help finding the
 * next line. They are stated here rather than in CSS because the input is a measurement, not a
 * media condition — a 380 px column on a MacBook in spread mode is *narrow*, and no viewport
 * query can know that.
 */
export function measureBucket(columnCh: number): MeasureBucket {
  if (columnCh < 48) return "narrow";
  if (columnCh < 68) return "normal";
  return "wide";
}

/* ══════════════════════════════════════════════════════════════════════════
 * The hook
 * ══════════════════════════════════════════════════════════════════════════ */

export interface Typography {
  prefs: TypographyPrefs;
  setPrefs(next: Partial<TypographyPrefs>): void;
  /** Step the reading size. Clamped; returns silently at the ends rather than wrapping. */
  stepScale(delta: number): void;
  scale: number;
  /** True once the browser reports every face for the chosen family is loaded (or that none exist). */
  fontsReady: boolean;
  /**
   * Changes if and only if something that can legitimately change the column count changed. Two
   * relayouts at an identical signature that disagree about `cols` is §5.3's `scrollWidth`
   * instability falsifier, and `repaginate()` reports it.
   */
  signature: string;
  /** Write every typography custom property and data attribute onto the reader root. */
  applyTo(root: HTMLElement | null): void;
}

export function useTypography(): Typography {
  const [prefs, setPrefsState] = useState<TypographyPrefs>(loadPrefs);
  const [fontsReady, setFontsReady] = useState(false);
  const [systemDark, setSystemDark] = useState(prefersDarkNow);
  const generation = useRef(0);

  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const on = () => setSystemDark(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const setPrefs = useCallback((next: Partial<TypographyPrefs>) => {
    setPrefsState((prev) => {
      const merged = { ...prev, ...next };
      savePrefs(merged);
      return merged;
    });
  }, []);

  const stepScale = useCallback(
    (delta: number) => {
      setPrefsState((prev) => {
        const i = Math.max(0, Math.min(READER_SCALES.length - 1, prev.scaleIndex + delta));
        if (i === prev.scaleIndex) return prev;
        const merged = { ...prev, scaleIndex: i };
        savePrefs(merged);
        return merged;
      });
    },
    [],
  );

  /**
   * Load the chosen family, then wait for the font system to go quiet.
   *
   * `document.fonts.load()` is what actually *starts* a fetch for a face that CSS has declared but
   * nothing has used yet; `document.fonts.ready` alone resolves immediately in that state and the
   * measurement would be taken against the fallback. Both, in that order, and only then is
   * `fontsReady` true.
   *
   * With no `@fontsource-*` package installed (see ui/fonts.css — the packages are not in
   * package.json yet) `load()` resolves with an empty list and `ready` resolves on the next turn,
   * so this is correct and cheap today and becomes load-bearing the moment the faces land.
   */
  useEffect(() => {
    const gen = ++generation.current;
    let cancelled = false;
    setFontsReady(false);

    const option = READER_FONTS.find((f) => f.key === prefs.font) ?? READER_FONTS[0]!;
    const size = `${READER_SCALES[prefs.scaleIndex] ?? 1}rem`;

    const run = async () => {
      try {
        await Promise.all(
          option.families.map((family) =>
            document.fonts.load(`${size} "${family}"`).catch(() => []),
          ),
        );
        await document.fonts.ready;
      } catch {
        /* A browser with no CSS Font Loading API. `ready` below still flips; we measure what we have. */
      }
      if (!cancelled && gen === generation.current) setFontsReady(true);
    };
    void run();

    return () => {
      cancelled = true;
    };
  }, [prefs.font, prefs.scaleIndex]);

  const scale = READER_SCALES[prefs.scaleIndex] ?? 1;

  const signature = useMemo(
    () => `${prefs.font}|${scale}|${prefs.justify ? "j" : "-"}|${prefs.spread}|${fontsReady ? "f" : "-"}`,
    [prefs.font, scale, prefs.justify, prefs.spread, fontsReady],
  );

  const applyTo = useCallback(
    (root: HTMLElement | null) => {
      if (!root) return;
      // `--reader-scale` and not `--read-scale`: the lifted `.read` rule owns `--read-scale`, and
      // writing the same custom property from two places is how a lifted file stops being liftable.
      root.style.setProperty("--reader-scale", String(scale));
      root.dataset.readFont = prefs.font;

      // **"auto" is resolved here, in JS, to a concrete theme.**
      //
      // books' reader.css defines the four palettes on `.read` and on `[data-read-theme="…"]`, and
      // leaves "auto" as *no attribute* so that `.dark .read:not([data-read-theme])` can pick it
      // up. That works there because the reading column is the only thing that needs the palette.
      // Here the reader ROOT needs it too — it paints the page behind the frame, the drawer, the
      // footer and the pill — and `.read`'s variables are not visible on an ancestor of `.read`.
      // Resolving to a concrete value puts `--read-bg` and friends on the root, where they cascade
      // down to everything including the column, and it costs one media query instead of a second
      // copy of four measured palettes.
      root.dataset.readTheme = prefs.theme === "auto" ? (isDark(root, systemDark) ? "night" : "day") : prefs.theme;
    },
    [prefs.font, prefs.theme, scale, systemDark],
  );

  // ⚠️ Memoised, and it is not a micro-optimisation. `Reader` derives `applyTypography` and then
  // `runRepaginate` from this object, and the relayout `useLayoutEffect` depends on the latter. A
  // fresh object per render therefore means **a full forced relayout on every state change** —
  // including the `setColumn`/`setPinSid` that every page turn performs, which turns a 220 ms
  // slide into a slide plus a `scrollWidth` flush on a phone. Caught in the Chrome harness by a
  // page turn that landed and then jumped back.
  return useMemo(
    () => ({ prefs, setPrefs, stepScale, scale, fontsReady, signature, applyTo }),
    [prefs, setPrefs, stepScale, scale, fontsReady, signature, applyTo],
  );
}

function prefersDarkNow(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

/** An explicit app theme (`.dark` on an ancestor, books' convention) beats the OS preference. */
function isDark(root: HTMLElement, systemDark: boolean): boolean {
  if (root.closest(".dark")) return true;
  if (root.closest(".light")) return false;
  return systemDark;
}
